import Modifier from 'ember-modifier';
import { inject as service } from '@ember/service';
import { registerDestructor } from '@ember/destroyable';
import { isMobile } from 'mewe/shared/utils';

// Usage:
// <element {{tooltip-popup this.text}}>
// Optional named args:
// - selector: add listeners to matching children instead of the root element
// - delay: hover delay before opening (ms), default 300
export default class TooltipPopupModifier extends Modifier {
  @service dynamicPopups;

  elements = [];
  delay = 300;
  _timeoutId = null;

  constructor() {
    super(...arguments);
    registerDestructor(this, () => this.teardown());
  }

  modify(element, positionalArgs = [], namedArgs = {}) {
    this.element = element;
    this.text = namedArgs.text ?? positionalArgs?.[0];
    this.selector = namedArgs.selector;
    this.allowTap = namedArgs.allowTap ?? false;
    this.htmlContent = namedArgs.htmlContent ?? false;
    this.bottom = namedArgs.bottom ?? false;
    this.delay = typeof namedArgs.delay === 'number' ? namedArgs.delay : 300;

    this.removeListeners();

    if (namedArgs.disabled) {
      return;
    }

    this.addListeners();
  }

  addListeners() {
    this.elements = this.selector ? this.element.querySelectorAll(this.selector) : [this.element];
    if (!this.elements || !this.elements.length) return;

    this.onEnter = this.onEnter?.bind ? this.onEnter.bind(this) : (e) => this.onEnter(e);
    this.onLeave = this.onLeave?.bind ? this.onLeave.bind(this) : (e) => this.onLeave(e);

    this.elements.forEach((el) => {
      el.addEventListener('mouseenter', this.onEnter);
      el.addEventListener('mouseleave', this.onLeave);
    });
  }

  removeListeners() {
    if (!this.elements || !this.elements.length) return;
    this.elements.forEach((el) => {
      el.removeEventListener('mouseenter', this.onEnter);
      el.removeEventListener('mouseleave', this.onLeave);
    });
    this.elements = [];
  }

  teardown() {
    this.clearTimer();
    this.removeListeners();
    // Ensure tooltip is closed when modifier is destroyed
    this.dynamicPopups?.closeByPopupName?.('mw-tooltip-popup');
  }

  clearTimer() {
    if (this._timeoutId) {
      clearTimeout(this._timeoutId);
      this._timeoutId = null;
    }
  }

  onEnter(e) {
    // Prevent multiple timers
    if (this._timeoutId) return;

    const target = e.currentTarget || e.target;
    this._timeoutId = setTimeout(() => {
      this.openTooltip(target);
      this._timeoutId = null;
    }, this.delay);
  }

  onLeave() {
    this.clearTimer();
    this.dynamicPopups.closeByPopupName('mw-tooltip-popup');
  }

  openTooltip(targetEl) {
    if (!targetEl) return;
    if (!this.text && !this.htmlContent) return;
    if (isMobile() && !this.allowTap) return; // tooltips on mobile devices shouldn't be shown as there is no hover state

    const dropdownId = `tooltip-${Math.random().toString(36).slice(2)}`;
    const params = {
      parent: targetEl,
      dropdownId,
      text: this.text,
      bottom: this.bottom,
      htmlContent: this.htmlContent,
    };

    // Pass this modifier instance as ctx so the service can resolve owner
    this.dynamicPopups.openPopup('mw-tooltip-popup', params, this);
  }
}


